/**
 * Bundle Cart Handling with Undo Feature
 * Handles bulk removal of items sharing the same _bundle_id.
 * Uses Event Delegation to support Drawer Cart and dynamic updates.
 * [NEW] Supports "Undo" functionality without page reload.
 */
(function () {
    console.log("RF Cart: Bundle Logic Loaded (Undo Mode)");

    // Store for recently deleted bundles (for undo functionality)
    let deletedBundleData = null;
    let undoTimeout = null;
    const UNDO_TIMEOUT_MS = 30000; // 30 seconds

    // [FIX] Resolve actual cart ROW container(s) for a bundle (the remove link also carries data-bundle-id)
    const getBundleRows = (bundleId) => {
        const sel = '.bundle-row-wrapper[data-bundle-id="' + bundleId + '"], tr.cart-item-row[data-bundle-id="' + bundleId + '"], line-item[data-bundle-id="' + bundleId + '"]';
        return Array.from(document.querySelectorAll(sel));
    };

    // [HELPER] Find relevant cart items by _bundle_id
    const findBundleItems = async (bundleId) => {
        try {
            const res = await fetch(window.Shopify.routes.root + 'cart.js');
            const cart = await res.json();
            const items = [];

            cart.items.forEach(item => {
                if (item.properties && item.properties['_bundle_id'] === bundleId) {
                    items.push({
                        key: item.key,
                        id: item.variant_id,
                        quantity: item.quantity,
                        properties: item.properties
                    });
                }
            });

            return items;
        } catch (err) {
            console.error("RF Cart: Failed to fetch cart", err);
            return [];
        }
    };

    // [HELPER] Update cart display (totals, etc.) - minimal update without triggering theme reloads
    const refreshCartUI = async () => {
        try {
            const res = await fetch(window.Shopify.routes.root + 'cart.js');
            const cart = await res.json();

            // Update cart count badges (various theme selectors)
            document.querySelectorAll('[data-cart-count], .cart-count, .js-cart-count, #cart-icon-bubble span').forEach(el => {
                el.textContent = cart.item_count;
                if (cart.item_count === 0) {
                    el.style.display = 'none';
                } else {
                    el.style.display = '';
                }
            });

            // Update cart total (various theme selectors)
            const formatMoney = (cents) => `¥${Math.round(cents / 100).toLocaleString()}`;
            document.querySelectorAll('[data-cart-total], .cart-total, .js-cart-total').forEach(el => {
                el.textContent = formatMoney(cart.total_price);
            });

            // [NOTE] Do NOT dispatch cart:change or cart:refresh events here
            // as they trigger theme code that expects section bundling data.
            // The undo UI handles its own visual state.

            console.log("RF Cart: UI updated, item_count:", cart.item_count, "total:", cart.total_price);
            return cart;
        } catch (err) {
            console.error("RF Cart: Failed to refresh UI", err);
            return null;
        }
    };

    // [HELPER] Create Undo UI
    const createUndoUI = (bundleRow, bundleId, productTitle) => {
        const undoContainer = document.createElement('div');
        undoContainer.className = 'rf-undo-container';
        undoContainer.setAttribute('data-undo-bundle-id', bundleId);
        undoContainer.innerHTML = `
            <div class="rf-undo-message">
                <span>「${productTitle}」を削除しました</span>
                <button type="button" class="rf-undo-btn" data-undo-bundle-id="${bundleId}">
                    元に戻す
                </button>
                <span class="rf-undo-timer">30秒</span>
            </div>
        `;

        // Insert undo UI where the row was
        if (bundleRow.parentNode) {
            bundleRow.parentNode.insertBefore(undoContainer, bundleRow);
        }

        // Hide ALL rows belonging to this bundle (a bundle may span multiple line items)
        const _rows = getBundleRows(bundleId);
        const _targets = _rows.length > 0 ? _rows : [bundleRow];
        _targets.forEach(r => {
            if (!r) return;
            r.style.display = 'none';
            r.setAttribute('data-deleted', 'true');
        });

        // Start countdown timer
        let remaining = 30;
        const timerEl = undoContainer.querySelector('.rf-undo-timer');
        const countdownInterval = setInterval(() => {
            remaining--;
            if (timerEl) timerEl.textContent = `${remaining}秒`;
            if (remaining <= 0) {
                clearInterval(countdownInterval);
            }
        }, 1000);

        return { undoContainer, countdownInterval };
    };

    // [HELPER] Restore deleted items
    const restoreBundle = async (bundleData) => {
        if (!bundleData || !bundleData.items || bundleData.items.length === 0) {
            console.warn("RF Cart: No bundle data to restore");
            return false;
        }

        console.log("RF Cart: Restoring bundle", bundleData);

        try {
            // Add all items back to cart
            const itemsToAdd = bundleData.items.map(item => ({
                id: item.id,
                quantity: item.quantity,
                properties: item.properties
            }));

            const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: itemsToAdd })
            });

            if (res.ok) {
                console.log("RF Cart: Bundle restored successfully");
                // Reload to show restored items correctly
                window.location.reload();
                return true;
            } else {
                console.error("RF Cart: Failed to restore bundle");
                return false;
            }
        } catch (err) {
            console.error("RF Cart: Restore error", err);
            return false;
        }
    };

    // [HELPER] Finalize deletion (remove undo UI, clear data)
    const finalizeDelete = (bundleId) => {
        // Remove undo UI
        const undoContainer = document.querySelector(`[data-undo-bundle-id="${bundleId}"]`);
        if (undoContainer) {
            undoContainer.remove();
        }

        // Remove ALL hidden rows for this bundle
        document.querySelectorAll(`[data-bundle-id="${bundleId}"][data-deleted="true"]`).forEach(el => el.remove());

        // Clear stored data
        if (deletedBundleData && deletedBundleData.bundleId === bundleId) {
            deletedBundleData = null;
        }

        // Check if cart is empty and show empty state
        const remainingItems = document.querySelectorAll('.cart-item-row:not([data-deleted="true"]), line-item:not([data-deleted="true"])');
        if (remainingItems.length === 0) {
            // Trigger empty cart state (reload is simplest)
            window.location.reload();
        }
    };

    // [CORE] Global Click Handler (Event Delegation)
    document.addEventListener('click', async function (e) {
        // --- Handle Undo Button Click ---
        const undoBtn = e.target.closest('.rf-undo-btn');
        if (undoBtn) {
            e.preventDefault();
            const bundleId = undoBtn.getAttribute('data-undo-bundle-id');

            if (deletedBundleData && deletedBundleData.bundleId === bundleId) {
                // Clear timeout
                if (undoTimeout) {
                    clearTimeout(undoTimeout);
                    undoTimeout = null;
                }

                // Show loading state
                undoBtn.textContent = '復元中...';
                undoBtn.disabled = true;

                // Restore the bundle
                await restoreBundle(deletedBundleData);
            }
            return;
        }

        // --- Handle Delete Button Click ---
        const link = e.target.closest('.js-bundle-remove');
        if (!link) return;

        const bundleId = link.getAttribute('data-bundle-id');
        if (!bundleId) return;

        // Stop propagation to prevent theme scripts
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        console.log("RF Cart: Intercepted Bundle Removal for", bundleId);

        // Find the bundle row
        // [FIX] Resolve the actual ROW container, not the link itself (link also has data-bundle-id)
        const _bundleRows = getBundleRows(bundleId);
        const bundleRow = _bundleRows[0] || link.closest('.bundle-row-wrapper') || link.closest('tr.cart-item-row') || link.closest('line-item');
        const productTitle = bundleRow?.querySelector('.line-item__title, .product-title, h4, h3')?.textContent?.trim() || '商品';

        // UI Feedback
        link.style.pointerEvents = 'none';
        link.style.opacity = '0.5';
        link.textContent = '削除中...';

        // Fetch and store bundle items BEFORE deletion (for undo)
        const bundleItems = await findBundleItems(bundleId);

        if (bundleItems.length === 0) {
            console.warn("RF Cart: No bundled items found. Fallback to default.");
            window.location.href = link.href;
            return;
        }

        // Store for undo
        deletedBundleData = {
            bundleId: bundleId,
            items: bundleItems,
            productTitle: productTitle
        };

        // Create updates object for deletion
        const updates = {};
        bundleItems.forEach(item => {
            updates[item.key] = 0;
        });

        console.log("RF Cart: Sending Batch Updates", updates);

        try {
            const updateRes = await fetch(window.Shopify.routes.root + 'cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates })
            });

            if (updateRes.ok) {
                console.log("RF Cart: Items removed from cart");

                // Create Undo UI
                const { undoContainer, countdownInterval } = createUndoUI(bundleRow, bundleId, productTitle);

                // Update cart UI (totals, counts)
                await refreshCartUI();

                // Set timeout to finalize deletion
                undoTimeout = setTimeout(() => {
                    clearInterval(countdownInterval);
                    finalizeDelete(bundleId);
                    deletedBundleData = null;
                }, UNDO_TIMEOUT_MS);

            } else {
                console.error("RF Cart: Update failed");
                window.location.reload();
            }

        } catch (err) {
            console.error("RF Cart: Batch Update Error", err);
            window.location.reload();
        }
    }, true); // Capture phase

    console.log("RF Cart: Delegation Listener Attached (Undo Mode)");
})();
